import { XMarkIcon } from '@heroicons/react/16/solid';
import { Form, Formik, FormikProps } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import {
  INTERACTIVE_TYPES,
  tInteractiveOutbound,
  tInteractiveType,
} from '../../../../Chat.types';
import { useMessageBarContext } from '../../MessageBar.hook';
import CTAUrlBuilder from './components/CTAUrlBuilder/CTAUrlBuilder.component';
import CommonFields from './components/CommonFields/CommonFields.component';
import ListBuilder from './components/ListBuilder/ListBuilder.component';
import PreviewBubble from './components/PreviewBubble/PreviewBubble.component';
import ReplyButtonsBuilder from './components/ReplyButtonsBuilder/ReplyButtonsBuilder.component';
import { EMPTY_ACTIONS } from './InteractiveAction.functions';
import {
  useInteractiveBodySync,
  useInteractiveComposer,
  useInteractiveDraftPersistence,
  useInteractiveSubmitPublisher,
} from './InteractiveAction.hook';
import { interactiveOutboundSchema } from './InteractiveAction.schema';
import './InteractiveAction.styles.scss';

// Render-null aggregator. Each concern lives in its own hook; this
// component exists because all three hooks need to run inside the
// <Formik> render-prop subtree (they call `useFormikContext`), so the
// natural home is a child component rather than the parent.
function InteractiveFormikBridge({
  authId,
  chatId,
  isSending,
  workspaceId,
}: {
  authId: string;
  chatId: string;
  isSending: boolean;
  workspaceId: string;
}) {
  useInteractiveBodySync();
  useInteractiveSubmitPublisher(isSending);
  useInteractiveDraftPersistence(authId, workspaceId, chatId);
  return null;
}

type tInteractiveActionProps = {
  preload?: tInteractiveOutbound;
};

export default function InteractiveAction(props: tInteractiveActionProps) {
  const { t } = useTranslation(['chat/chat']);
  const { trackWapiEvent } = useWapiAnalytics();
  const { messageBarValue } = useMessageBarContext();
  const {
    authId,
    chatId,
    currentWorkspaceId,
    initialValues,
    fieldErrors,
    handleClose,
    handleSend,
    isSending,
  } = useInteractiveComposer({ preload: props.preload });

  // Seed the composer's body with whatever the seller already typed in the
  // main chat input, so opening the composer mid-draft doesn't wipe their
  // text. Formik uses `enableReinitialize={false}` so this is consumed
  // once on mount — memoize to avoid reconstructing the object on every
  // render (Formik ignores it after mount, but the allocation is waste).
  const seededInitialValues = useMemo<tInteractiveOutbound>(
    () => ({
      ...initialValues,
      body: initialValues.body || messageBarValue || '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className="InteractiveAction">
      <div className="InteractiveAction__Header">
        <p className="InteractiveAction__Header__Title">
          {t('interactive.title')}
        </p>
        <XMarkIcon
          onClick={handleClose}
          className="InteractiveAction__Header__Icon"
        />
      </div>

      <Formik<tInteractiveOutbound>
        initialValues={seededInitialValues}
        validationSchema={interactiveOutboundSchema}
        validateOnMount
        enableReinitialize={false}
        onSubmit={handleSend}
      >
        {(formik: FormikProps<tInteractiveOutbound>) => (
          // Split container: form on the visual-left in expanded view (60%)
          // and preview on the visual-right (40%); stacks with preview above
          // form in normal view via CSS variable set by ChatModal.
          <div className="InteractiveAction__Split">
          <Form className="InteractiveAction__Form">
            <InteractiveFormikBridge
              authId={authId}
              chatId={chatId}
              isSending={isSending}
              workspaceId={currentWorkspaceId}
            />
            <fieldset className="InteractiveAction__TypePicker">
              <legend className="InteractiveAction__TypePicker__Legend">
                {t('interactive.typePicker.legend')}
              </legend>
              {INTERACTIVE_TYPES.map((type) => (
                <label key={type} className="InteractiveAction__TypePicker__Option">
                  <input
                    type="radio"
                    name="type"
                    value={type}
                    checked={formik.values.type === type}
                    className="radio radio-primary radio-sm"
                    onChange={() => {
                      // Reset action + header when switching type so the
                      // discriminated union stays valid. Headers aren't
                      // preserved across type switches because the list
                      // variant restricts to `tInteractiveTextHeader` (with
                      // required `text`) while button/cta_url allow richer
                      // media headers — cross-variant narrowing would lose
                      // the invariant and trip the compiler. Seller UX
                      // tradeoff: minor, type switch is rare after initial
                      // pick.
                      const nextType = type as tInteractiveType;
                      if (nextType === 'button') {
                        formik.setValues({
                          type: 'button',
                          body: formik.values.body,
                          footer: formik.values.footer,
                          action: EMPTY_ACTIONS.button,
                        });
                      } else if (nextType === 'cta_url') {
                        formik.setValues({
                          type: 'cta_url',
                          body: formik.values.body,
                          footer: formik.values.footer,
                          action: EMPTY_ACTIONS.cta_url,
                        });
                      } else {
                        formik.setValues({
                          type: 'list',
                          body: formik.values.body,
                          footer: formik.values.footer,
                          action: EMPTY_ACTIONS.list,
                        });
                      }
                      trackWapiEvent(
                        WAPI_EVENTS.CHAT_INTERACTIVE_TYPE_SELECTED,
                        { type: nextType },
                      );
                    }}
                  />
                  <span>{t(`interactive.typePicker.options.${type}`)}</span>
                </label>
              ))}
            </fieldset>

            <CommonFields />

            {formik.values.type === 'button' && <ReplyButtonsBuilder />}
            {formik.values.type === 'cta_url' && <CTAUrlBuilder />}
            {formik.values.type === 'list' && <ListBuilder />}

            {fieldErrors && fieldErrors.length > 0 && (
              <div className="InteractiveAction__ServerErrors">
                <p className="InteractiveAction__ServerErrors__Title">
                  {t('interactive.serverErrors.title')}
                </p>
                <ul>
                  {fieldErrors.map((e) => (
                    <li
                      key={`${e.field}-${e.code}`}
                      className="InteractiveAction__ServerErrors__Item"
                    >
                      <code>{e.field}</code>: {e.code}
                      {e.max !== undefined && e.actual !== undefined
                        ? ` (max ${e.max}, got ${e.actual})`
                        : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No submit button here — SendAction (paper-airplane icon in
                the main chat input area) drives the submit via
                interactiveSubmitRef, so we don't duplicate the Enviar
                button. InteractiveFormikBridge publishes isValid so
                SendAction can toggle its disabled state. */}
          </Form>

            <PreviewBubble />
          </div>
        )}
      </Formik>
    </div>
  );
}
